import React from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { LuChevronLeft } from "react-icons/lu";
import { FooterMenu } from "./FooterMenu";
import { cn } from "../components/ui";
import { t } from "../i18n";

interface PageLayoutProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Sem onBack, o cabeçalho não mostra o botão de voltar. */
  onBack?: () => void;
  /** Ação secundária à direita do título (ex.: "Pedidos salvos"). */
  action?: React.ReactNode;
  /** Ação principal, fixa embaixo e sempre no mesmo lugar. */
  cta?: React.ReactNode;
  /** Por que a ação principal está desabilitada — escrito logo abaixo dela. */
  ctaHint?: React.ReactNode;
  /** Menu inferior. As telas do meio do fluxo do pedido não mostram. */
  nav?: boolean;
  children: React.ReactNode;
}

const COLUMN = "mx-auto w-full max-w-page px-4";

/** Layout único das telas do app: cabeçalho fixo, coluna de conteúdo, ação principal fixa e menu inferior. */
export const PageLayout: React.FC<PageLayoutProps> = ({ title, subtitle, onBack, action, cta, ctaHint, nav = true, children }) => {
  const { pathname } = useLocation();
  // Voltar (botão do navegador ou navigate(-1)) entra pela esquerda; avançar, pela direita.
  const enter = useNavigationType() === "POP" ? "animate-page-back" : "animate-page-forward";

  return (
    <div className={cn("min-h-full bg-surface text-body text-fg", nav && "pb-nav")}>
      <header className="sticky top-banner z-20 border-b border-line bg-surface/95 backdrop-blur">
        <div className={cn(COLUMN, "flex min-h-header items-center gap-2.5 py-2")}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("common.back")}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line text-primary transition-colors hover:bg-primary-tint focus-visible:outline-none focus-visible:shadow-focus"
            >
              <LuChevronLeft size={22} aria-hidden />
            </button>
          )}
          <h1 className="min-w-0 flex-1 truncate text-title-sm font-semibold">
            {title}
            {subtitle && <span className="block truncate text-caption font-medium text-fg-subtle">{subtitle}</span>}
          </h1>
          {action}
        </div>
      </header>

      <main className={cn(COLUMN, "flex flex-col gap-4 pb-6 pt-2", enter)}>
        {children}

        {cta && (
          <div className={cn("sticky z-10 -mx-4 mt-1 flex flex-col items-stretch gap-1.5 bg-gradient-to-t from-surface from-70% to-transparent px-4 pt-3.5 sm:items-center", nav ? "bottom-nav pb-2" : "bottom-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]")}>
            <div className="flex gap-2.5 sm:w-full sm:max-w-md [&>*]:flex-1">{cta}</div>
            <p className="min-h-5 text-center text-caption text-fg-muted" aria-live="polite">
              {ctaHint}
            </p>
          </div>
        )}
      </main>

      {nav && <FooterMenu current={pathname} />}
    </div>
  );
};
